import React, { useEffect, useState, useRef } from "react";
import "@google/model-viewer";

type ModelViewerProps = {
  modelPath: string;
  rotationY?: number;
  iosSrc?: string | null;
  setLoading?: (loading: boolean) => void;
  onAnimationControlsChange?: (
    controls: {
      hasExplodedAnimation: boolean;
      isDisassembled: boolean;
      isAnimating: boolean;
      isSpinning: boolean;
      handleDisassemble: () => void;
      handleAssemble: () => void;
      handle360Spin: () => void;
    } | null
  ) => void;
};

const ModelViewer: React.FC<ModelViewerProps> = ({
  modelPath,
  rotationY,
  iosSrc,
  onAnimationControlsChange,
}) => {
  const [, setLoading] = useState(true);
  const [hasExplodedAnimation, setHasExplodedAnimation] = useState(false);
  const [isDisassembled, setIsDisassembled] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const modelViewerRef = useRef<any>(null);
  const finishedHandlerRef = useRef<any>(null);
  const playHandlerRef = useRef<any>(null);
  const isAnimatingRef = useRef<boolean>(false);
  const spinIntervalRef = useRef<any>(null);
  const fallbackTimeoutRef = useRef<any>(null);
  const animationRunIdRef = useRef<number>(0);

  useEffect(() => {
    if (setLoading) {
      setLoading(true);
    }

    setIsModelLoaded(false);
    setHasExplodedAnimation(false);
    setIsDisassembled(false);
    setIsAnimating(false);
    setIsSpinning(false);
    isAnimatingRef.current = false;

    if (spinIntervalRef.current) {
      clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = null;
    }

    const modelViewer = modelViewerRef.current;

    const handleModelLoad = () => {
      if (setLoading) {
        setLoading(false);
      }

      setIsModelLoaded(true);
      console.log("Model loaded successfully");

      const src = (modelViewer as any).src;
      const iosSrcAttr = (modelViewer as any).getAttribute("ios-src");

      console.log("Model source:", src);
      console.log("iOS source:", iosSrcAttr);

      if (src && src.toLowerCase().endsWith(".glb")) {
        console.log("Viewing format: GLB");
      } else if (src && src.toLowerCase().endsWith(".gltf")) {
        console.log("Viewing format: GLTF");
      } else if (iosSrcAttr && iosSrcAttr.toLowerCase().endsWith(".usdz")) {
        console.log("Viewing format: USDZ (iOS)");
      } else {
        console.log("Viewing format: Unknown");
      }

      const animations = (modelViewer as any).availableAnimations;
      if (animations && animations.length > 0) {
        const explodedAnim = animations.find((anim: string) =>
          anim.toLowerCase().includes("exploded")
        );

        if (explodedAnim && modelViewer) {
          console.log("Found exploded animation:", explodedAnim);
          console.log("Initial paused state:", (modelViewer as any).paused);
          console.log("Initial currentTime:", (modelViewer as any).currentTime);

          setHasExplodedAnimation(true);

          (modelViewer as any).pause();

          (modelViewer as any).animationName = explodedAnim;
          (modelViewer as any).currentTime = 0;

          console.log("After setup - paused:", (modelViewer as any).paused);
          console.log(
            "After setup - currentTime:",
            (modelViewer as any).currentTime
          );

          const handleFinished = () => {
            console.log("=== FINISHED EVENT FIRED ===");
            if (modelViewer) {
              if ((modelViewer as any)._fallbackTimeout) {
                console.log("Clearing fallback timeout");
                clearTimeout((modelViewer as any)._fallbackTimeout);
                (modelViewer as any)._fallbackTimeout = null;
              }
              if (fallbackTimeoutRef.current) {
                clearTimeout(fallbackTimeoutRef.current);
                fallbackTimeoutRef.current = null;
              }

              const timeScale = (modelViewer as any).timeScale;
              const currentTime = (modelViewer as any).currentTime;
              const duration = (modelViewer as any).duration;
              const paused = (modelViewer as any).paused;

              console.log("TimeScale:", timeScale);
              console.log("CurrentTime:", currentTime);
              console.log("Duration:", duration);
              console.log("Paused:", paused);
              console.log("isAnimatingRef.current:", isAnimatingRef.current);

              let nextDisassembled = isDisassembled;
              if (isAnimatingRef.current) {
                console.log("Processing finish - updating state");
                if (timeScale > 0) {
                  console.log("Setting isDisassembled to true");
                  nextDisassembled = true;
                  setIsDisassembled(true);
                } else if (timeScale < 0) {
                  console.log("Setting isDisassembled to false");
                  nextDisassembled = false;
                  setIsDisassembled(false);
                }
              } else {
                console.log(
                  "Finished event observed without user-initiated animation"
                );
              }
              console.log("Setting isAnimating to false");
              setIsAnimating(false);
              isAnimatingRef.current = false;
              console.log(
                "State updated - isAnimatingRef.current now:",
                isAnimatingRef.current
              );

              if (onAnimationControlsChange) {
                if (hasExplodedAnimation) {
                  onAnimationControlsChange({
                    hasExplodedAnimation,
                    isDisassembled: nextDisassembled,
                    isAnimating: false,
                    isSpinning,
                    handleDisassemble,
                    handleAssemble,
                    handle360Spin,
                  });
                } else {
                  onAnimationControlsChange({
                    hasExplodedAnimation: false,
                    isDisassembled: false,
                    isAnimating: false,
                    isSpinning,
                    handleDisassemble,
                    handleAssemble,
                    handle360Spin,
                  });
                }
              }
            }
            console.log("=== FINISHED EVENT COMPLETE ===");
          };

          const handlePlay = () => {
            console.log("Play event fired");
            console.log("isAnimatingRef.current:", isAnimatingRef.current);
            console.log("currentTime:", (modelViewer as any).currentTime);

            if (!isAnimatingRef.current) {
              console.log("Unexpected play detected - pausing");
              (modelViewer as any).pause();
              (modelViewer as any).currentTime = 0;
            }
          };

          finishedHandlerRef.current = handleFinished;
          playHandlerRef.current = handlePlay;
          modelViewer.addEventListener("finished", handleFinished);
          modelViewer.addEventListener("play", handlePlay);
        } else {
          setHasExplodedAnimation(false);
        }
      } else {
        setHasExplodedAnimation(false);
      }
    };

    if (modelViewer) {
      modelViewer.addEventListener("load", handleModelLoad);
      modelViewer.setAttribute("ios-src", iosSrc || "");
    } else {
      console.error("Failed to find model-viewer element.");
    }

    if (iosSrc) {
      setLoading(false);
    } else {
      setLoading(true);
    }

    return () => {
      if (modelViewer) {
        modelViewer.removeEventListener("load", handleModelLoad);
        if (finishedHandlerRef.current) {
          modelViewer.removeEventListener(
            "finished",
            finishedHandlerRef.current
          );
        }
        if (playHandlerRef.current) {
          modelViewer.removeEventListener("play", playHandlerRef.current);
        }

        // Clear any fallback timeouts
        if ((modelViewer as any)._fallbackTimeout) {
          clearTimeout((modelViewer as any)._fallbackTimeout);
        }
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
      }

      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
      }
    };
  }, [modelPath, iosSrc]);

  const handleDisassemble = () => {
    console.log("=== DISASSEMBLE CLICKED ===");
    console.log("isAnimating:", isAnimating);
    console.log("isAnimatingRef.current:", isAnimatingRef.current);

    if (modelViewerRef.current && !isAnimating) {
      const modelViewer = modelViewerRef.current;
      const rawDuration = Number(modelViewer.duration);
      const safeDuration =
        Number.isFinite(rawDuration) && rawDuration > 0 && rawDuration < 120
          ? rawDuration
          : 3;

      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
      if (isSpinning) {
        setIsSpinning(false);
      }

      console.log("Setting isAnimating to true");
      setIsAnimating(true);
      isAnimatingRef.current = true;

      if (onAnimationControlsChange) {
        if (hasExplodedAnimation) {
          onAnimationControlsChange({
            hasExplodedAnimation,
            isDisassembled,
            isAnimating: true,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        } else {
          onAnimationControlsChange({
            hasExplodedAnimation: false,
            isDisassembled: false,
            isAnimating: true,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        }
      }

      console.log("isAnimatingRef.current after set:", isAnimatingRef.current);
      console.log(
        "Animation duration (raw):",
        rawDuration,
        "(safe):",
        safeDuration
      );

      modelViewer.timeScale = 1;
      modelViewer.play({ repetitions: 1 });

      console.log("Play called - timeScale:", modelViewer.timeScale);

      if ((modelViewer as any)._fallbackTimeout) {
        clearTimeout((modelViewer as any)._fallbackTimeout);
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }

      animationRunIdRef.current += 1;
      const runId = animationRunIdRef.current;

      const delayMs = (safeDuration + 0.5) * 1000;
      const fallbackTimeout = setTimeout(() => {
        if (animationRunIdRef.current !== runId) {
          console.log("Fallback ignored due to newer animation run");
          return;
        }
        console.log("=== FALLBACK TIMEOUT TRIGGERED ===");
        console.log("Finished event didn't fire, resetting state manually");
        setIsDisassembled(true);
        setIsAnimating(false);
        isAnimatingRef.current = false;
        console.log("State reset via fallback");

        if (onAnimationControlsChange) {
          onAnimationControlsChange({
            hasExplodedAnimation,
            isDisassembled: true,
            isAnimating: false,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        }
      }, delayMs);

      (modelViewer as any)._fallbackTimeout = fallbackTimeout;
      fallbackTimeoutRef.current = fallbackTimeout;
    } else {
      console.log("Not executing - isAnimating is true or no modelViewer");
    }
  };

  const handleAssemble = () => {
    console.log("=== ASSEMBLE CLICKED ===");
    console.log("isAnimating:", isAnimating);
    console.log("isAnimatingRef.current:", isAnimatingRef.current);

    if (modelViewerRef.current && !isAnimating) {
      const modelViewer = modelViewerRef.current;
      const rawDuration = Number(modelViewer.duration);
      const safeDuration =
        Number.isFinite(rawDuration) && rawDuration > 0 && rawDuration < 120
          ? rawDuration
          : 3;

      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
      if (isSpinning) {
        setIsSpinning(false);
      }

      console.log("Setting isAnimating to true");
      setIsAnimating(true);
      isAnimatingRef.current = true;

      if (onAnimationControlsChange) {
        if (hasExplodedAnimation) {
          onAnimationControlsChange({
            hasExplodedAnimation,
            isDisassembled,
            isAnimating: true,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        } else {
          onAnimationControlsChange({
            hasExplodedAnimation: false,
            isDisassembled: false,
            isAnimating: true,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        }
      }

      console.log("isAnimatingRef.current after set:", isAnimatingRef.current);
      console.log(
        "Animation duration (raw):",
        rawDuration,
        "(safe):",
        safeDuration
      );

      modelViewer.timeScale = -1;
      modelViewer.play({ repetitions: 1 });

      console.log("Play called - timeScale:", modelViewer.timeScale);

      if ((modelViewer as any)._fallbackTimeout) {
        clearTimeout((modelViewer as any)._fallbackTimeout);
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }

      animationRunIdRef.current += 1;
      const runId = animationRunIdRef.current;

      const delayMs = (safeDuration + 0.5) * 1000;
      const fallbackTimeout = setTimeout(() => {
        if (animationRunIdRef.current !== runId) {
          console.log("Fallback ignored due to newer animation run");
          return;
        }
        console.log("=== FALLBACK TIMEOUT TRIGGERED (REVERSE) ===");
        console.log("Finished event didn't fire, resetting state manually");
        setIsDisassembled(false);
        setIsAnimating(false);
        isAnimatingRef.current = false;
        console.log("State reset via fallback (reverse)");

        if (onAnimationControlsChange) {
          onAnimationControlsChange({
            hasExplodedAnimation,
            isDisassembled: false,
            isAnimating: false,
            isSpinning,
            handleDisassemble,
            handleAssemble,
            handle360Spin,
          });
        }
      }, delayMs);

      (modelViewer as any)._fallbackTimeout = fallbackTimeout;
      fallbackTimeoutRef.current = fallbackTimeout;
    } else {
      console.log("Not executing - isAnimating is true or no modelViewer");
    }
  };

  const handle360Spin = () => {
    console.log("=== 360 SPIN CLICKED ===");
    console.log("isSpinning:", isSpinning);
    console.log(
      "isAnimating:",
      isAnimating,
      "isAnimatingRef:",
      isAnimatingRef.current
    );

    if (
      modelViewerRef.current &&
      !isSpinning &&
      !isAnimating &&
      !isAnimatingRef.current
    ) {
      const modelViewer = modelViewerRef.current;

      setIsSpinning(true);

      const currentOrbit = modelViewer.getCameraOrbit();
      const startTheta = currentOrbit.theta;
      const targetTheta = startTheta + 360;
      const duration = 3000;
      const startTime = Date.now();

      console.log("Starting 360 spin from theta:", startTheta);

      spinIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easedProgress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        const newTheta = startTheta + 360 * easedProgress;

        modelViewer.cameraOrbit = `${newTheta}deg ${currentOrbit.phi}rad ${currentOrbit.radius}m`;

        if (progress >= 1) {
          console.log("360 spin completed");
          clearInterval(spinIntervalRef.current);
          spinIntervalRef.current = null;
          setIsSpinning(false);
        }
      }, 16);
    } else {
      console.log(
        "Spin not started - another animation is running or already spinning"
      );
    }
  };

  useEffect(() => {
    if (onAnimationControlsChange && isModelLoaded) {
      if (hasExplodedAnimation) {
        onAnimationControlsChange({
          hasExplodedAnimation,
          isDisassembled,
          isAnimating,
          isSpinning,
          handleDisassemble,
          handleAssemble,
          handle360Spin,
        });
      } else {
        onAnimationControlsChange({
          hasExplodedAnimation: false,
          isDisassembled: false,
          isAnimating: false,
          isSpinning,
          handleDisassemble: () => {},
          handleAssemble: () => {},
          handle360Spin,
        });
      }
    }
  }, [
    hasExplodedAnimation,
    isDisassembled,
    isAnimating,
    isSpinning,
    isModelLoaded,
    onAnimationControlsChange,
  ]);

  return (
    <div
      className="dark:bg-black"
      style={{
        width: "100%",
        height: "70vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <model-viewer
        ref={modelViewerRef}
        ios-src={iosSrc || undefined}
        src={modelPath}
        alt="3D model"
        autoplay={!hasExplodedAnimation}
        ar
        rotation={`0 ${rotationY || 0} 0`}
        auto-rotate
        camera-controls
        style={{
          width: "100%",
          height: "100%",
          marginTop: "-40px",
        }}
      />
    </div>
  );
};

export default ModelViewer;
